import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { apiFetch } from "../../lib/api-client";

export const Route = createFileRoute("/_app/review")({
  component: ReviewPage,
});

type LearningItem = {
  id: string;
  mode: "object" | "grammar";
  text: string;
  imageUrl: string | null;
  imageProvider: string | null;
  imageModel: string | null;
  costUsd: number | null;
  wrongText: string | null;
  grammarKind: "wrong" | "less-natural" | null;
  createdAt: string;
};

const fetchItems = (): Promise<LearningItem[]> =>
  apiFetch<LearningItem[]>("/items");

const deleteItem = (id: string): Promise<{ id: string }> =>
  apiFetch<{ id: string }>(`/items/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

const formatCost = (cost: number | null): string => {
  if (cost === null) return "?";
  if (cost === 0) return "free";
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(3)}`;
};

function ReviewPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["items"],
    queryFn: fetchItems,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: (_, id) => {
      qc.setQueryData<LearningItem[]>(["items"], (prev) =>
        prev ? prev.filter((it) => it.id !== id) : prev,
      );
    },
  });

  const onDelete = (item: LearningItem) => {
    const label =
      item.mode === "grammar" && item.wrongText
        ? `${item.text} / ${item.wrongText}`
        : item.text;
    if (!window.confirm(`"${label}" を削除しますか？`)) return;
    deleteMutation.mutate(item.id);
  };

  const totalCost = data?.reduce((sum, it) => sum + (it.costUsd ?? 0), 0) ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">復習</h1>
        {data && data.length > 0 && (
          <p className="text-xs text-neutral-500">
            合計 {data.length} 件 / 累計 ${totalCost.toFixed(3)}
          </p>
        )}
      </div>
      <p className="text-sm text-neutral-600">
        今までに教えたものの履歴（間隔反復は実装予定）
      </p>

      {isLoading && <LoadingIndicator label="読み込み中..." />}
      {error && (
        <p className="text-sm text-red-600">{(error as Error).message}</p>
      )}

      {data && data.length === 0 && (
        <div className="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-neutral-500">
          まだ何もありません
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.map((item) => (
            <div
              key={item.id}
              className="rounded-xl overflow-hidden border border-neutral-200 bg-white relative"
            >
              {item.imageUrl && (
                <img
                  src={item.imageUrl}
                  alt={item.text}
                  className={
                    item.mode === "grammar"
                      ? "w-full aspect-video object-cover"
                      : "w-full aspect-square object-cover"
                  }
                />
              )}
              <button
                type="button"
                onClick={() => onDelete(item)}
                disabled={deleteMutation.isPending}
                className="absolute top-2 right-2 rounded-full bg-white/90 hover:bg-red-500 hover:text-white text-neutral-600 w-8 h-8 flex items-center justify-center text-sm shadow border border-neutral-200 transition-colors disabled:opacity-50"
                aria-label="削除"
                title="削除"
              >
                ×
              </button>
              <div className="p-3 space-y-2">
                {item.mode === "grammar" && item.wrongText ? (
                  <div className="space-y-1">
                    <div className="text-sm">
                      <span className="text-rose-700 font-semibold">
                        {item.grammarKind === "less-natural" ? "△" : "❌"}
                      </span>{" "}
                      {item.wrongText}
                    </div>
                    <div className="text-sm">
                      <span className="text-emerald-700 font-semibold">
                        {item.grammarKind === "less-natural" ? "◯" : "⭕"}
                      </span>{" "}
                      {item.text}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm font-medium truncate">
                    {item.text}
                  </div>
                )}
                <div className="flex items-center justify-between text-[10px] text-neutral-500">
                  <span title={item.imageModel ?? ""} className="truncate">
                    {item.imageProvider ?? "?"}
                    {item.imageModel ? ` · ${item.imageModel}` : ""}
                  </span>
                  <span className="shrink-0 ml-2">
                    {formatCost(item.costUsd)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
